import React, { useEffect, useState } from 'react'
import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'

const NewClaimPage: React.FC = () => {
  const currentUser = useQuery(api.auth.getCurrentUser)
  // Fetch current user id lazily when submitting
  const fetchPolicy = useAction(api.pasApiService.fetchPolicyData as any)
  const searchPolicies = useAction(api.pasApiService.searchPolicies as any)
  const createClaim = useMutation(api.claims.createClaimFromForm as any)

  const [claimType, setClaimType] = useState<'life' | 'disability' | ''>('')
  const [policyNumber, setPolicyNumber] = useState('')
  const [dateOfDeath, setDateOfDeath] = useState('')
  const [causeOfDeath, setCauseOfDeath] = useState('')
  const [disabilityOnsetDate, setDisabilityOnsetDate] = useState('')
  const [disabilityType, setDisabilityType] = useState('')
  const [medicalProvider, setMedicalProvider] = useState('')
  const [workRestrictions, setWorkRestrictions] = useState('')
  
  const [reportedDate, setReportedDate] = useState('')
  const [estimatedValue, setEstimatedValue] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [policyInfo, setPolicyInfo] = useState<any>(null)
  const [policySearchResults, setPolicySearchResults] = useState<any[]>([])
  const [showPolicyDropdown, setShowPolicyDropdown] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<string>('')
  const [submitError, setSubmitError] = useState<string>('')

  // When claim type changes, clear hidden-type fields and related errors
  useEffect(() => {
    setErrors((prev) => {
      const next = { ...prev }
      delete next.dateOfDeath
      delete next.causeOfDeath
      delete next.disabilityOnsetDate
      delete next.disabilityType
      return next
    })
    if (claimType === 'life') {
      // Clear disability-only fields when switching to life
      setDisabilityOnsetDate('')
      setDisabilityType('')
      setMedicalProvider('')
      setWorkRestrictions('')
    } else if (claimType === 'disability') {
      // Clear life-only fields when switching to disability
      setDateOfDeath('')
      setCauseOfDeath('')
    }
  }, [claimType])

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    if (!claimType) e.claimType = 'Claim type is required'
    if (!policyNumber) e.policyNumber = 'Policy number is required'
    if (!reportedDate) e.reportedDate = 'Reported date required'
    if (claimType === 'life') {
      if (!dateOfDeath) e.dateOfDeath = 'Date of death is required'
      if (!causeOfDeath) e.causeOfDeath = 'Cause of death is required'
    }
    if (claimType === 'disability') {
      if (!disabilityOnsetDate) e.disabilityOnsetDate = 'Disability onset date is required'
      if (!disabilityType) e.disabilityType = 'Disability type is required'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const onBlurPolicy = async () => {
    if (!policyNumber) return
    const res = await fetchPolicy({ policyNumber, noRetry: true }) as any
    if (res?.success && res.data) {
      setPolicyInfo(res.data)
      setErrors((prev) => { const { policyNumber, ...rest } = prev; return rest })
      setShowPolicyDropdown(false)
    } else {
      setPolicyInfo(null)
      setErrors((prev) => ({ ...prev, policyNumber: res?.error || 'Policy not found in PAS' }))
    }
  }

  const onSearchPolicy = async (q: string) => {
    setPolicyNumber(q)
    setShowPolicyDropdown(true)
    const query = q && q.trim().length > 0 ? q : '*'
    const res = await searchPolicies({ query }) as any
    if (res?.success) setPolicySearchResults(res.results || [])
  }

  const submit = async () => {
    setSubmitError('')
    const createdBy = currentUser?._id
    if (!createdBy) {
      setSubmitError('Please log in to create a claim.')
      return
    }
    if (!createdBy) {
      setSubmitError('Unable to resolve a user. Please try again.')
      return
    }
    if (!validate()) return
    setSubmitting(true)
    try {
      const payload: any = {
        policyNumber,
        claimType,
        reportedDate: new Date(reportedDate).getTime(),
        createdBy,
      }
      if (estimatedValue) payload.estimatedValue = Number(estimatedValue)
      if (claimType === 'life') {
        payload.dateOfDeath = new Date(dateOfDeath).getTime()
        payload.causeOfDeath = causeOfDeath
      } else if (claimType === 'disability') {
        payload.disabilityOnsetDate = new Date(disabilityOnsetDate).getTime()
        payload.disabilityType = disabilityType
        payload.medicalProvider = medicalProvider
        payload.workRestrictions = workRestrictions
      }
      // Attach read-only PAS snapshot fields if available
      if (policyInfo) {
        payload.insuredFirstName = policyInfo.insuredFirstName
        payload.insuredLastName = policyInfo.insuredLastName
        payload.policyHolderFirstName = policyInfo.policyHolderFirstName
        payload.policyHolderLastName = policyInfo.policyHolderLastName
        if (Array.isArray(policyInfo.beneficiariesSnapshot)) {
          payload.beneficiaries = policyInfo.beneficiariesSnapshot
        }
      }
      const res = await createClaim(payload) as any
      if (res?.claimNumber) {
        setSuccess('Claim created successfully')
        window.location.href = `/claims/${res.claimNumber}`
      } else {
        setSubmitError('Failed to create claim. Please try again.')
      }
    } catch (e: any) {
      setSubmitError(e?.message || 'An unexpected error occurred while creating the claim.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="new-claim-page">
      <div className="page-header">
        <h1>Create New Claim</h1>
      </div>

      <div data-testid="claim-form" className="claim-form">
        <div className="form-grid">
          <div className="form-field">
            <label>Claim Type</label>
            <select data-testid="claim-type" value={claimType} onChange={(e) => setClaimType(e.target.value as any)}>
              <option value="">Select type</option>
              <option value="life">Life</option>
              <option value="disability">Disability</option>
            </select>
            {errors.claimType && <div data-testid="error-claim-type" className="error">{errors.claimType}</div>}
          </div>

          <div className="form-field" style={{ position: 'relative' }}>
            <label>Policy Number</label>
            <input
              data-testid="policy-number"
              value={policyNumber}
              onChange={(e) => onSearchPolicy(e.target.value)}
              onBlur={onBlurPolicy}
              onFocus={() => onSearchPolicy(policyNumber)}
              placeholder="Type to search policies (e.g. TERM, WHOLE, STD, LTD)"
            />
            {errors.policyNumber && <div data-testid="error-policy-number" className="error">{errors.policyNumber}</div>}
            {showPolicyDropdown && (
              <div className="policy-suggestions" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10 }}>
                {policySearchResults.length === 0 ? (
                  <div className="suggestion">No policies found</div>
                ) : (
                  policySearchResults.map((p) => (
                    <div key={p.policyNumber} className="suggestion" onMouseDown={() => { setPolicyNumber(p.policyNumber); setShowPolicyDropdown(false); setPolicySearchResults([]); onBlurPolicy(); }}>
                      {p.policyNumber} – {p.policyType} – {new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(p.benefitAmount)}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {policyInfo && (
            <>
              <div className="form-field">
                <label>Policy Type</label>
                <input readOnly value={policyInfo.policyType || ''} />
              </div>
              <div className="form-field">
                <label>Benefit Amount</label>
                <input readOnly data-testid="benefit-amount" value={new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(policyInfo.benefitAmount || 0)} />
              </div>
              <div className="form-field">
                <label>Policy Holder First Name</label>
                <input readOnly value={policyInfo.policyHolderFirstName || ''} />
              </div>
              <div className="form-field">
                <label>Policy Holder Last Name</label>
                <input readOnly value={policyInfo.policyHolderLastName || ''} />
              </div>
              <div className="form-field">
                <label>Insured First Name</label>
                <input readOnly value={policyInfo.insuredFirstName || ''} />
              </div>
              <div className="form-field">
                <label>Insured Last Name</label>
                <input readOnly value={policyInfo.insuredLastName || ''} />
              </div>
              <div className="form-field">
                <label>Coverage Summary</label>
                <textarea readOnly value={policyInfo.coverageSummary || ''} />
              </div>
              {Array.isArray(policyInfo.riders) && (
                <div className="form-field">
                  <label>Riders</label>
                  <textarea readOnly value={(policyInfo.riders as string[]).join('\n')} />
                </div>
              )}
              {Array.isArray(policyInfo.beneficiariesSnapshot) && policyInfo.beneficiariesSnapshot.length > 0 && (
                <div className="form-field">
                  <label>Beneficiaries</label>
                  <textarea readOnly value={policyInfo.beneficiariesSnapshot.map((b: any) => `${b.name} (${b.relationship}${b.percentage != null ? ` ${b.percentage}%` : ''})`).join('\n')} />
                </div>
              )}
            </>
          )}

          {/* claimant inputs removed; parties are from policy */}

          {claimType === 'life' && (
            <>
              <div className="form-field">
                <label>Date of Death</label>
                <input data-testid="date-of-death" type="date" value={dateOfDeath} onChange={(e) => setDateOfDeath(e.target.value)} />
                {errors.dateOfDeath && <div data-testid="error-date-of-death" className="error">{errors.dateOfDeath}</div>}
              </div>
              <div className="form-field">
                <label>Cause of Death</label>
                <input data-testid="cause-of-death" value={causeOfDeath} onChange={(e) => setCauseOfDeath(e.target.value)} />
                {errors.causeOfDeath && <div data-testid="error-cause-of-death" className="error">{errors.causeOfDeath}</div>}
              </div>
            </>
          )}

          {claimType === 'disability' && (
            <>
              <div className="form-field">
                <label>Disability Onset Date</label>
                <input data-testid="disability-onset-date" type="date" value={disabilityOnsetDate} onChange={(e) => setDisabilityOnsetDate(e.target.value)} />
                {errors.disabilityOnsetDate && <div className="error">{errors.disabilityOnsetDate}</div>}
              </div>
              <div className="form-field">
                <label>Disability Type</label>
                <select data-testid="disability-type" value={disabilityType} onChange={(e) => setDisabilityType(e.target.value)}>
                  <option value="">Select</option>
                  <option value="partial">Partial</option>
                  <option value="total">Total</option>
                  <option value="temporary">Temporary</option>
                  <option value="permanent">Permanent</option>
                </select>
                {errors.disabilityType && <div className="error">{errors.disabilityType}</div>}
              </div>
              <div className="form-field">
                <label>Medical Provider</label>
                <input data-testid="medical-provider" value={medicalProvider} onChange={(e) => setMedicalProvider(e.target.value)} />
              </div>
              <div className="form-field">
                <label>Work Restrictions</label>
                <input data-testid="work-restrictions" value={workRestrictions} onChange={(e) => setWorkRestrictions(e.target.value)} />
              </div>
            </>
          )}

          <div className="form-field">
            <label>Reported Date</label>
            <input type="date" value={reportedDate} onChange={(e) => setReportedDate(e.target.value)} />
            {errors.reportedDate && <div className="error">{errors.reportedDate}</div>}
          </div>

          <div className="form-field">
            <label>Estimated Value (CAD)</label>
            <input value={estimatedValue} onChange={(e) => setEstimatedValue(e.target.value)} />
          </div>
        </div>

        <button data-testid="submit-claim" disabled={submitting} onClick={submit} className="primary">
          {submitting ? 'Submitting...' : 'Submit Claim'}
        </button>

        {submitError && <div data-testid="submit-error" className="error" style={{ marginTop: 8 }}>{submitError}</div>}
        {success && <div data-testid="success-message" className="success">{success}</div>}
      </div>
    </div>
  )
}

export default NewClaimPage


